/// <reference types="vite/client" />
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Version.scss';

/** Injected by each app's vite.config.ts (`define`). */
declare const __APP_VERSION__: string;

interface VersionProps {
  backendVersion?: string;
  aiServiceVersion?: string;
  /**
   * 'floating' pins the badge to the bottom-right of the viewport (default);
   * 'inline' renders it in flow — e.g. inside the header — with the detail
   * panel dropping below the badge instead of pushing the layout around.
   */
  variant?: 'floating' | 'inline';
  /**
   * Inline only: where the detail panel opens. 'below' (default) hangs it
   * under the badge, centred; 'above' raises it from the badge's start edge,
   * for a badge at the bottom of a sidebar.
   */
  placement?: 'below' | 'above';
}

export function Version({
  backendVersion = '',
  aiServiceVersion = '',
  variant = 'floating',
  placement = 'below',
}: VersionProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const versionRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const closeExpanded = () => {
    setIsExpanded(false);
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        versionRef.current &&
        !versionRef.current.contains(event.target as Node)
      ) {
        closeExpanded();
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const isInline = variant === 'inline';
  const opensAbove = isInline && placement === 'above';

  const feVersion = __APP_VERSION__ || 'N/A';
  const beVersion = backendVersion ? backendVersion.substring(0, 7) : 'N/A';
  const aiVersion = aiServiceVersion ? aiServiceVersion.substring(0, 7) : 'N/A';

  if (
    !import.meta.env.VITE_SHOW_VERSION ||
    import.meta.env.VITE_SHOW_VERSION === 'false' ||
    localStorage.getItem('showVersion') === undefined
  ) {
    return null;
  }

  const rows = [
    { key: 'frontend', label: t('Frontend'), value: feVersion, show: true },
    {
      key: 'backend',
      label: t('Backend'),
      value: beVersion,
      show: Boolean(backendVersion),
    },
    {
      key: 'ai',
      label: t('AI Service'),
      value: aiVersion,
      show: Boolean(aiServiceVersion),
    },
  ].filter((row) => row.show);

  const placementClass = opensAbove
    ? 'version--above'
    : isInline
    ? 'version--below'
    : 'version--floating';

  return (
    <div ref={versionRef} className={`version ${placementClass}`}>
      {!isExpanded && (
        <button
          type="button"
          className="version__badge"
          onClick={toggleExpanded}
          aria-expanded={false}
        >
          <span>
            {t('v')}
            {feVersion}
          </span>
          <svg
            className="version__caret"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M7 14l5-5 5 5z" />
          </svg>
        </button>
      )}

      {isExpanded && (
        <div className="version__panel">
          <div className="version__header">
            <h3 className="version__title">{t('Version Info')}</h3>
            <button
              type="button"
              className="version__close"
              onClick={closeExpanded}
              aria-label={t('Close')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19 13H5v-2h14v2z" />
              </svg>
            </button>
          </div>

          <div className="version__rows">
            {rows.map((row) => (
              <div key={row.key} className="version__row">
                <span className="version__label">{row.label}</span>
                <span className={`version__value version__value--${row.key}`}>
                  {t('v')}
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          <div className="version__built">
            {t('Built:')} {new Date().toLocaleDateString()}
          </div>
        </div>
      )}
    </div>
  );
}
